import { motion } from "framer-motion";
import { BellRing, CheckCheck, Flag, Pause, Trophy, OctagonX } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, EASE } from "@/components/Reveal";

const NOTIFS = [
  { icon: BellRing, title: "TRADE IDENTIFIED", sub: "NQ LONG", time: "now", tone: "neon" },
  { icon: CheckCheck, title: "TRADE VALIDATED", sub: "Conditions confirmed", time: "2m", tone: "neon" },
  { icon: Flag, title: "TP1 HIT", sub: "First target reached", time: "11m", tone: "neon" },
  { icon: Pause, title: "HOLD FOR TP2", sub: "Process says hold", time: "12m", tone: "white" },
  { icon: Trophy, title: "TP2 HIT", sub: "Trade complete", time: "26m", tone: "neon" },
  { icon: OctagonX, title: "INVALIDATED — EXIT", sub: "Conditions no longer valid", time: "1h", tone: "risk" },
];

const toneMap = {
  neon: "border-neon/40 text-neon",
  white: "border-line text-white",
  risk: "border-risk/40 text-risk",
};

export default function Alerts() {
  return (
    <section data-testid="alerts-section" className="py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
        <div>
          <SectionHeading
            chapter="05 — Alerts"
            testid="alerts-heading"
            title={
              <>
                Stay informed.
                <br />
                Without staying <span className="text-neon">glued to the chart.</span>
              </>
            }
            description="Follow the execution lifecycle with structured alerts designed to keep you informed as market conditions change."
          />
          <Reveal delay={0.2}>
            <p className="mt-6 font-mono text-[11px] uppercase leading-relaxed tracking-[0.18em] text-dim">
              Alerts describe trade status — they do not imply guaranteed
              outcomes.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="relative mx-auto w-full max-w-[320px]">
            <div className="absolute -inset-8 rounded-[3rem] bg-neon/[0.04] blur-3xl" />
            <div
              data-testid="alerts-phone-mockup"
              className="relative rounded-[2.6rem] border border-line bg-charcoal p-2.5 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.9)]"
            >
              <div className="overflow-hidden rounded-[2.1rem] border border-line bg-ink">
                <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-dim">
                    P.E.E. OS
                  </span>
                  <span className="h-1.5 w-14 rounded-full bg-line" />
                  <span className="font-mono text-[10px] text-neon">LIVE</span>
                </div>
                <div className="space-y-2.5 p-4">
                  {NOTIFS.map((n, i) => (
                    <motion.div
                      key={n.title}
                      data-testid={`alert-notification-${i}`}
                      initial={{ opacity: 0, x: 44 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.7, delay: 0.1 * i, ease: EASE }}
                      className={`flex items-start gap-3 rounded-lg border bg-charcoal/80 p-3 ${toneMap[n.tone]}`}
                    >
                      <n.icon size={16} className="mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-[8px] uppercase tracking-[0.22em] text-dim">
                          P.E.E. OS
                        </div>
                        <div className="mt-0.5 truncate font-mono text-[11px] font-semibold tracking-wide">
                          {n.title}
                        </div>
                        <div className="truncate font-mono text-[10px] text-mist">
                          {n.sub}
                        </div>
                      </div>
                      <span className="font-mono text-[9px] text-dim">{n.time}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
